import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import clsx from "clsx";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ApiError, api } from "@/shared/api/client";
import { invalidate } from "@/shared/lib/query";
import { errorMessage, fieldMessage } from "@/shared/i18n";
import { useCurrencies } from "@/shared/lib/session";
import { currencyChoices } from "./CurrencyField";
import { Field } from "./Field";
import { Modal } from "./Modal";
import { CountrySelect } from "./CountrySelect";
import { customBody, customSpecs, useCustomDefs } from "./CustomFields";
import { TagInput } from "./TagInput";
import { EntityPicker, type Option } from "./EntityPicker";
import { ErrorBox } from "./Page";

export type FieldType = "text" | "email" | "password" | "tel" | "number" | "money" | "date" | "datetime" | "textarea" | "select" | "bool" | "entity" | "currency" | "country" | "tags";

export interface FieldSpec {
  name: string;
  label: ReactNode;
  type?: FieldType;
  required?: boolean;
  options?: { value: string; label: ReactNode }[];
  entity?: { path: string; toOption: (row: never) => Option; filter?: Record<string, string>; plain?: boolean };
  span?: 1 | 2;
  hint?: ReactNode;
  ltr?: boolean;
  placeholder?: string;
  /** For tags: the ones already in use, offered as they type. */
  suggest?: string[];
  /** Hide unless the predicate on current values is true. */
  when?: (v: Values) => boolean;
  /**
   * Tucked under "More details" while it's empty: the form opens with what most records need.
   * "always" tucks it even when it has a value the form started with (a default, a yes/no).
   */
  more?: boolean | "always";
}

const filled = (v: unknown) => v !== undefined && v !== null && v !== "" && v !== false && v !== 0 && !(Array.isArray(v) && v.length === 0);

export type Values = Record<string, unknown>;

/** "2026-09-29T10:30" in the browser's time, as datetime-local inputs take it. */
function localInput(iso: string): string {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

/** Reads a dotted path ("address.city") from values. */
function get(v: Values, path: string): unknown {
  return path.split(".").reduce<unknown>((o, k) => (o && typeof o === "object" ? (o as Values)[k] : undefined), v);
}

function set(v: Values, path: string, value: unknown): Values {
  const [head, ...rest] = path.split(".");
  if (rest.length === 0) return { ...v, [head]: value };
  return { ...v, [head]: set((v[head] as Values) ?? {}, rest.join("."), value) };
}

/** Field errors from a 422 problem, keyed by field path. */
export function fieldErrors(err: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (err instanceof ApiError) {
    for (const f of err.fields) {
      const k = f.field.replace(/^body\./, "");
      if (k) out[k] = fieldMessage(f.message);
    }
  }
  return out;
}

export function ResourceFields({ fields, values, onChange, errors }: { fields: FieldSpec[]; values: Values; onChange: (v: Values) => void; errors: Record<string, string> }) {
  const { t } = useTranslation();
  const currencies = useCurrencies();
  // A currency field appears only when there is a currency to choose.
  const shown = (f: FieldSpec) => (!f.when || f.when(values)) && (f.type !== "currency" || currencyChoices(currencies, get(values, f.name)).length > 0);
  const [more, setMore] = useState(false);
  // A field that answers later (a picker adding what was typed) must not write over
  // what changed meanwhile: changes apply to the latest values, not the render's.
  const latest = useRef(values);
  latest.current = values;
  const tucked = fields.filter((f) => shown(f) && f.more && !more && (f.more === "always" || !filled(get(values, f.name))) && !errors[f.name]);
  return (
    <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2">
      {fields.filter((f) => shown(f) && !tucked.includes(f)).map((f) => {
        const val = get(values, f.name);
        const change = (x: unknown) => { latest.current = set(latest.current, f.name, x); onChange(latest.current); };
        const err = errors[f.name];
        const cls = clsx("field", err && "border-danger");
        // A yes/no is a card to tick, its label beside the box rather than over it.
        if (f.type === "bool") {
          return (
            <div key={f.name} className={clsx("self-end", f.span === 2 && "sm:col-span-2")}>
              <label className="flex cursor-pointer items-center gap-2.5 rounded-[var(--radius-ctl)] border border-line px-3 py-[9px] transition-colors select-none hover:bg-paper has-[:checked]:border-ledger/40 has-[:checked]:bg-ledger-soft/50">
                <input type="checkbox" className="size-4 accent-ledger" checked={!!val} onChange={(e) => change(e.target.checked)} />
                <span className="font-medium">{f.label}</span>
              </label>
              {err ? <span role="alert" className="mt-1 block text-[12px] text-danger">{err}</span> : f.hint ? <span className="mt-1 block text-[12px] text-ink-3">{f.hint}</span> : null}
            </div>
          );
        }
        let input: ReactNode;
        switch (f.type ?? "text") {
          case "textarea":
            input = <textarea className={clsx(cls, "min-h-20")} value={(val as string) ?? ""} onChange={(e) => change(e.target.value)} required={f.required} />;
            break;
          case "select":
            input = (
              <select className={cls} value={(val as string) ?? ""} onChange={(e) => change(e.target.value || null)} required={f.required}>
                {!f.required && <option value="">{t("common.none")}</option>}
                {f.options?.map((o) => <option key={o.value} value={o.value}>{o.label as string}</option>)}
              </select>
            );
            break;
          case "currency":
            input = (
              <select className={cls} value={(val as string) ?? ""} onChange={(e) => change(e.target.value || null)} required={f.required}>
                {!f.required && <option value="">{t("common.none")}</option>}
                {currencyChoices(currencies, val).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            );
            break;
          case "tags":
            input = <TagInput value={(val as string[] | undefined) ?? []} onChange={change} suggest={f.suggest} invalid={!!err} label={typeof f.label === "string" ? f.label : undefined} />;
            break;
          case "country":
            input = <CountrySelect value={(val as string) ?? ""} onChange={(c) => change(c || null)} invalid={!!err} first={f.placeholder} />;
            break;
          case "entity":
            input = (
              <EntityPicker
                path={f.entity!.path}
                value={val as string | null}
                onChange={(id) => change(id)}
                toOption={f.entity!.toOption as (r: { id: string }) => Option}
                filter={f.entity!.filter}
                plain={f.entity!.plain}
                placeholder={f.placeholder}
                invalid={!!err}
              />
            );
            break;
          case "number":
          case "money":
            input = (
              <input className={clsx(cls, "num")} inputMode="decimal" dir="ltr" value={(val as string | number | undefined) ?? ""} required={f.required}
                onChange={(e) => change(f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} />
            );
            break;
          case "date":
            input = <input className={cls} type="date" value={(val as string) ?? ""} onChange={(e) => change(e.target.value || null)} required={f.required} />;
            break;
          case "datetime":
            input = <input className={cls} type="datetime-local" value={val ? localInput(val as string) : ""} required={f.required}
              onChange={(e) => change(e.target.value ? new Date(e.target.value).toISOString() : null)} />;
            break;
          default:
            input = (
              <input className={cls} type={f.type ?? "text"} dir={f.ltr || f.type === "email" || f.type === "tel" ? "ltr" : undefined} autoComplete={f.type === "password" ? "new-password" : undefined}
                value={(val as string) ?? ""} required={f.required} placeholder={f.placeholder} onChange={(e) => change(e.target.value)} />
            );
        }
        return (
          <Field key={f.name} label={f.label} required={f.required} error={err} hint={f.hint} className={f.span === 2 || f.type === "textarea" ? "sm:col-span-2" : undefined}>
            {input}
          </Field>
        );
      })}
      {(tucked.length > 0 || more) && fields.some((f) => f.more) && (
        <button type="button" className="btn-ghost justify-self-start py-0.5 text-ledger sm:col-span-2" aria-expanded={more} onClick={() => setMore(!more)}>
          {more ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
          {more ? t("common.lessDetails") : t("common.moreDetails", { count: tucked.length })}
        </button>
      )}
    </div>
  );
}

/** Create/edit modal for a crud resource. PUT sends the full input (with If-Match). */
export function ResourceModal(props: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** One line under the title: what the record is for (when creating, often the list's first-use hint). */
  description?: ReactNode;
  path: string;
  fields: FieldSpec[];
  initial: Values;
  id?: string;
  etag?: string;
  /** Maps form values to the request body. */
  toBody?: (v: Values) => Values;
  onSaved?: (rec: { id: string }) => void;
  invalidate?: string[];
  size?: "md" | "lg";
  /** The entity (core.organization, crm.lead, …) whose custom fields the form shows after its
   * own; the record's are in initial.custom. */
  custom?: string;
  /** Shown under the fields as they're filled in (a warning that the record may exist). */
  notice?: (v: Values) => ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const defs = useCustomDefs(props.custom).data ?? [];
  const fields = props.custom ? [...props.fields, ...customSpecs(defs, i18n.language)] : props.fields;
  const qc = useQueryClient();
  const [values, setValues] = useState<Values>(props.initial);
  const [touched, setTouched] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (props.open) { setValues(props.initial); setErr(null); setTouched(false); }
  }, [props.open]); // eslint-disable-line react-hooks/exhaustive-deps
  // Defaults that arrive after the form opens (the signed-in user, say) fill it
  // until the user has typed in it.
  const initialKey = JSON.stringify(props.initial);
  useEffect(() => {
    if (props.open && !touched) setValues(props.initial);
  }, [initialKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const errors = fieldErrors(err);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const base = props.toBody ? props.toBody(values) : values;
      // Only the custom fields defined now: a value left from a removed one would be refused.
      const body = props.custom ? { ...base, custom: customBody(defs, values.custom as Record<string, unknown> | undefined) } : base;
      const rec = props.id
        ? await api.put<{ id: string }>(`${props.path}/${props.id}`, body, props.etag)
        : await api.post<{ id: string }>(props.path, body);
      await invalidate(qc, [props.path]);
      for (const k of props.invalidate ?? []) await invalidate(qc, [k]);
      toast.success(t("common.saved"));
      props.onSaved?.(rec);
      props.onClose();
    } catch (x) {
      setErr(x);
      if (!(x instanceof ApiError && x.fields.length)) toast.error(errorMessage(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={props.open} onClose={props.onClose} title={props.title} description={props.description} size={props.size ?? "md"}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={props.onClose}>{t("common.cancel")}</button>
          <button type="submit" form="resource-form" className="btn-primary" disabled={busy}>{props.id ? t("common.save") : t("common.create")}</button>
        </>
      }>
      <form id="resource-form" onSubmit={submit} className="space-y-3">
        {err != null && Object.keys(errors).length === 0 && <ErrorBox error={err} />}
        <ResourceFields fields={fields} values={values} onChange={(v) => { setTouched(true); setValues(v); }} errors={errors} />
        {props.notice?.(values)}
      </form>
    </Modal>
  );
}

/** Copies only the named top-level keys (drops read-only fields before PUT). */
export function pick(rec: object, keys: string[]): Values {
  const out: Values = {};
  for (const k of keys) out[k] = (rec as Values)[k];
  return out;
}
